<script>
export default { name: 'UiTextarea' };
</script>

<script setup>
import { computed } from 'vue';

let uidSeed = 0;

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  label: { type: String, default: '' },
  required: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  rows: { type: [String, Number], default: 4 },
  disabled: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  error: { type: String, default: '' },
  hint: { type: String, default: '' },
  // Componente lucide a mostrar arriba a la izquierda, ej. :icon="MapPin"
  icon: { type: [Object, Function, String], default: null },
  maxlength: { type: [String, Number], default: null },
  id: { type: String, default: null },
});

const emit = defineEmits(['update:modelValue', 'change', 'focus', 'blur']);

const fieldId = props.id || `ui-textarea-${++uidSeed}`;

const textareaClass = computed(() => [
  'w-full rounded-xl border bg-white dark:bg-slate-950 px-3 py-2.5 text-sm text-slate-900 dark:text-slate-100 shadow-sm transition-all outline-none placeholder-slate-400 dark:placeholder-slate-600 focus:ring-4 resize-y',
  props.error
    ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10'
    : 'border-slate-200 dark:border-slate-700 focus:border-brand-500 focus:ring-brand-500/10',
  props.readonly ? 'bg-slate-50 dark:bg-slate-800/60 cursor-not-allowed' : '',
  props.icon ? 'pl-10' : '',
]);
</script>

<template>
  <div>
    <label v-if="label" :for="fieldId" class="block text-xs font-semibold text-slate-500 uppercase mb-1">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <div class="relative">
      <component
        v-if="icon"
        :is="icon"
        class="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none"
        aria-hidden="true"
      />

      <textarea
        :id="fieldId"
        :value="props.modelValue"
        :placeholder="placeholder"
        :rows="rows"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :maxlength="maxlength"
        :class="textareaClass"
        @input="emit('update:modelValue', $event.target.value)"
        @change="$emit('change', $event)"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
      />
    </div>

    <p v-if="error" class="text-xs text-red-500 mt-1">{{ error }}</p>
    <p v-else-if="hint" class="text-xs text-slate-400 mt-1">{{ hint }}</p>
  </div>
</template>